/**
 * batch-69 bestelflow (FO-512, spec 212) — de pure regels van de bestelflow-
 * delta's. Geen DB, geen IO: de API (bevestigen, preview, de status-tijdlijn) en
 * het verwerkscherm rekenen met exact dezelfde functies, zodat scherm en server
 * het nooit oneens zijn over wat er geblokkeerd is.
 *
 *  1. Extern bestellen alleen voor het deel dat NIET op voorraad ligt (§B.3,
 *     §5.1/§5.2): ligt er vrije voorraad van deze maat, dan moet dat deel uit
 *     voorraad, of eerst gecorrigeerd naar 0 (telling klopt niet).
 *  2. De tijdelijke route "voorraadtelling in opbouw": uit voorraad leveren bij
 *     stand 0 kan alleen tot en met de einddatum (NL-kalenderdag) en niet als
 *     de schakelaar uit staat (§B.3, A2).
 *  3. Het verplichte minimumniveau (§C): "niet op voorraad" afhandelen (een extern
 *     deel, of elke "al geleverd"-afhandeling, FO-467) vraagt een bewust
 *     vastgesteld minimum van de voorraadbron. 0 telt als bewuste keuze wanneer
 *     de laatste minimum-wijziging op 0 uitkwam.
 *  4. De status-tijdlijn (§A): fase, vaste stappen, per regel uit voorraad /
 *     extern besteld / geleverd, en de gebeurtenissen; in de publieke variant
 *     (portal) zonder leverancier en zonder interne notities.
 *
 * Tijdzone: dagen in Europe/Amsterdam (CLAUDE.md); de DB blijft UTC.
 */

import {
  BESTELLING_STAP_LABELS,
  bepaalBestellingStappen,
  UITRUSTING_BESTELLING_AUDIT_LABELS,
  UITRUSTING_PUBLIEKE_AUDIT_ACTIES,
  UITRUSTING_STATUS_FASE_LABELS,
  VOORRAADTELLING_IN_OPBOUW_STANDAARD_TOT,
  type UitrustingBestellingStatus,
  type UitrustingRegelStatus,
  type UitrustingStatusFase,
  type UitrustingStatusTijdlijnDTO,
  type UitrustingTijdlijnGebeurtenisDTO,
  type UitrustingTijdlijnRegelDTO,
  type UitrustingTijdlijnStapDTO,
  type UitrustingVoorraadgevolg,
  type UitrustingZendingStatus,
} from '@streep/types'
import { nlDagSleutel } from './vanysselt-saldo'

// ─── 1. Extern bij vrije voorraad ───────────────────────────────────────────

export interface ExternGrens {
  /** Zoveel moet minimaal uit voorraad: de vrije voorraad, begrensd op het aantal. */
  minVoorraad: number
  /** Zoveel mag maximaal extern: het deel dat niet op voorraad ligt. */
  maxExtern: number
}

/**
 * Hoeveel van een regel mag extern besteld worden? Alleen het deel dat niet op
 * voorraad ligt (spec 212 §5.2: 3 van 5 op voorraad = 3 uit voorraad, 2 extern).
 * De grens geldt per maat-variant: de caller geeft de vrije voorraad van DEZE
 * bron, niet van het artikel (§5.1).
 */
export function bepaalExternGrens(aantal: number, vrij: number): ExternGrens {
  const n = Math.max(0, Math.floor(aantal))
  const minVoorraad = Math.min(n, Math.max(0, Math.floor(vrij)))
  return { minVoorraad, maxExtern: n - minVoorraad }
}

/**
 * Is deze verdeling geblokkeerd omdat er extern besteld wordt terwijl er nog
 * vrije voorraad ligt? `aantalVoorraad` is het gevraagde voorraaddeel.
 */
export function isExternBijVrijeVoorraad(input: {
  aantal: number
  aantalVoorraad: number
  vrij: number
}): boolean {
  const { minVoorraad } = bepaalExternGrens(input.aantal, input.vrij)
  const gevraagd = Math.max(0, Math.min(Math.floor(input.aantalVoorraad), input.aantal))
  const extern = input.aantal - gevraagd
  return extern > 0 && gevraagd < minVoorraad
}

// ─── 2. Voorraadtelling in opbouw (tijdelijke route) ────────────────────────

export interface VoorraadtellingKeuze {
  /** De door beheer gekozen einddatum (yyyy-mm-dd, NL) of null = de standaard. */
  tot: string | null
  /** Bewust uitgeschakeld. */
  uit: boolean
}

export interface VoorraadtellingStand {
  actief: boolean
  tot: string
  standaard: boolean
  uit: boolean
}

/**
 * De stand van de tijdelijke route op moment `nu`. Actief = niet bewust uit en
 * de NL-kalenderdag van `nu` valt op of vóór de einddatum (die dag telt mee).
 */
export function bepaalVoorraadtellingStand(
  keuze: VoorraadtellingKeuze,
  nu: Date,
): VoorraadtellingStand {
  const standaard = keuze.tot === null
  const tot = keuze.tot ?? VOORRAADTELLING_IN_OPBOUW_STANDAARD_TOT
  const actief = !keuze.uit && nlDagSleutel(nu) <= tot
  return { actief, tot, standaard, uit: keuze.uit }
}

// ─── 3. Minimumniveau ───────────────────────────────────────────────────────

/**
 * Is het minimum van een voorraadbron bewust vastgesteld? Een minimum boven 0
 * altijd; 0 alleen als de laatste vastlegging van de niveaus (audit) op 0
 * uitkwam. Een 0 zonder vastlegging is de standaard van een nieuw artikel: nooit
 * ingesteld.
 */
export function isMinimumVastgesteld(
  minimumVoorraad: number,
  laatsteWijzigingNaar: number | null,
): boolean {
  if (minimumVoorraad > 0) return true
  return laatsteWijzigingNaar !== null && laatsteWijzigingNaar === minimumVoorraad
}

/**
 * Wordt een regel afgehandeld als "niet op voorraad"? Spec 212 §C letterlijk:
 * "extern of FO-467". Dus bij een extern deel, en bij elke "al geleverd"-
 * afhandeling, welk voorraadgevolg ook (geen mutatie, voorraad al bijgewerkt of
 * afboeken): de regel liep niet via de gewone voorraadroute, en juist dan moet
 * het minimum van het artikel een bewuste keuze zijn. `alGeleverdGevolg` null =
 * de regel is niet als "al geleverd" gemarkeerd.
 */
export function isNietOpVoorraadAfhandeling(input: {
  externDeel: number
  alGeleverdGevolg: UitrustingVoorraadgevolg | null
}): boolean {
  if (input.alGeleverdGevolg !== null) return true
  return input.externDeel > 0
}

// ─── 4. Status-tijdlijn ─────────────────────────────────────────────────────

export interface StatusTijdlijnRegelBron {
  regelId: string
  omschrijving: string
  aantal: number
  aantalVoorraad: number
  aantalGeleverd: number
  regelStatus: UitrustingRegelStatus
  geleverdOp: string | null
  zending: {
    status: UitrustingZendingStatus
    verzondenOp: string | null
    leverancierNaam: string
    gewensteLeverdatum: string | null
  } | null
  /** Al geleverd zonder bestelling (FO-467): het voorraadgevolg; anders null/weggelaten. */
  alGeleverd?: UitrustingVoorraadgevolg | null
}

export interface StatusTijdlijnAuditBron {
  id: string
  action: string
  /** De bestelregel waar de gebeurtenis over gaat, of null (de hele bestelling). */
  regelId: string | null
  createdAt: string
  performedByNaam: string | null
  note: string | null
}

export interface StatusTijdlijnBron {
  bestellingId: string
  status: UitrustingBestellingStatus
  ingediendOp: string | null
  beoordeeldOp: string | null
  beoordeeldDoorNaam: string | null
  beoordelingNotitie: string | null
  opgepaktOp: string | null
  opgepaktDoorNaam: string | null
  verwerktOp: string | null
  verwerktDoorNaam: string | null
  geleverdOp: string | null
  regels: ReadonlyArray<StatusTijdlijnRegelBron>
  audits: ReadonlyArray<StatusTijdlijnAuditBron>
}

const VERWERKT_STATUSSEN: ReadonlySet<UitrustingBestellingStatus> = new Set([
  'VERWERKT',
  'VERZONDEN_LEVERANCIER',
  'GELEVERD',
])

/** De fase van de bestelling: GOEDGEKEURD met een claim heet "in verwerking". */
export function bepaalStatusFase(
  status: UitrustingBestellingStatus,
  opgepakt: boolean,
): UitrustingStatusFase {
  switch (status) {
    case 'CONCEPT':
      return 'CONCEPT'
    case 'INGEDIEND':
      return 'INGEDIEND'
    case 'GOEDGEKEURD':
      return opgepakt ? 'IN_VERWERKING' : 'GOEDGEKEURD'
    case 'VERWERKT':
    case 'VERZONDEN_LEVERANCIER':
      return 'VERWERKT'
    case 'GELEVERD':
      return 'GELEVERD'
    case 'AFGEKEURD':
      return 'AFGEKEURD'
    case 'GEANNULEERD':
      return 'GEANNULEERD'
  }
}

function stuks(n: number): string {
  return `${n} ${n === 1 ? 'stuk' : 'stuks'}`
}

/**
 * "3 uit voorraad, 2 extern besteld, 1 al geleverd" over de niet-geschrapte
 * regels. Een "al geleverd"-regel (FO-467) is niet besteld en niet klaargelegd.
 */
function verdelingTekst(regels: ReadonlyArray<StatusTijdlijnRegelBron>): string | null {
  let voorraad = 0
  let extern = 0
  let alGeleverd = 0
  for (const r of regels) {
    if (r.regelStatus === 'GESCHRAPT') continue
    if (r.alGeleverd) {
      alGeleverd += r.aantal
      continue
    }
    const v = Math.max(0, Math.min(r.aantalVoorraad, r.aantal))
    voorraad += v
    extern += r.aantal - v
  }
  const delen: string[] = []
  if (voorraad > 0) delen.push(`${stuks(voorraad)} uit voorraad`)
  if (extern > 0) delen.push(`${stuks(extern)} extern besteld`)
  if (alGeleverd > 0) delen.push(`${stuks(alGeleverd)} al geleverd`)
  return delen.length > 0 ? delen.join(', ') : null
}

function faseLabel(
  fase: UitrustingStatusFase,
  bron: StatusTijdlijnBron,
): string {
  if (fase === 'IN_VERWERKING') {
    return bron.opgepaktDoorNaam
      ? `In verwerking (opgepakt door ${bron.opgepaktDoorNaam})`
      : UITRUSTING_STATUS_FASE_LABELS.IN_VERWERKING
  }
  if (fase === 'VERWERKT') {
    const actief = bron.regels.filter((r) => r.regelStatus !== 'GESCHRAPT')
    // "Extern besteld" pas als de bestelmail echt verstuurd is (integrator-review
    // L3): een mislukte of nog wachtende zending is bij de leverancier niet besteld.
    const extern = actief.filter((r) => r.regelStatus === 'EXTERN_BESTELD')
    const verstuurd = extern.filter((r) => r.zending?.status === 'VERZONDEN').length
    const geleverd = actief.filter((r) => r.regelStatus === 'GELEVERD').length
    const basis =
      extern.length === 0
        ? 'Verwerkt: klaar in het magazijn'
        : verstuurd === 0
          ? 'Verwerkt: de bestelling bij de leverancier is nog niet verstuurd'
          : verstuurd < extern.length
            ? 'Verwerkt: extern besteld (een bestelling is nog niet verstuurd)'
            : 'Verwerkt: extern besteld'
    return geleverd > 0 ? `${basis}, deels geleverd` : basis
  }
  return UITRUSTING_STATUS_FASE_LABELS[fase]
}

/** Acties waarvan de notitie ook voor de aanvrager is (die kreeg hem al te horen). */
const PUBLIEKE_NOTITIE_ACTIES: ReadonlySet<string> = new Set(['AFGEKEURD', 'GEANNULEERD'])

/** Zonder voortgang (geen balk): de fase en de gebeurtenissen zeggen genoeg. */
const ZONDER_VOORTGANG: ReadonlySet<UitrustingBestellingStatus> = new Set(['CONCEPT', 'AFGEKEURD', 'GEANNULEERD'])

/**
 * Bouwt de status-tijdlijn. `publiek` = de weergave voor de aanvrager in de
 * portal: geen leverancier, alleen de gebeurtenissen over de eigen spullen, en
 * geen interne notities (bij een regel staat dan welk artikel het betreft).
 *
 * De stappen (en hun labels) komen uit `bepaalBestellingStappen`, dezelfde
 * afleiding als de balk in de portal-lijst en op het verwerkscherm.
 */
export function bouwStatusTijdlijn(
  bron: StatusTijdlijnBron,
  opts: { publiek: boolean },
): UitrustingStatusTijdlijnDTO {
  const opgepakt = bron.opgepaktOp !== null
  const fase = bepaalStatusFase(bron.status, opgepakt)
  const isVerwerkt = VERWERKT_STATUSSEN.has(bron.status)
  const actieveRegels = bron.regels.filter((r) => r.regelStatus !== 'GESCHRAPT')

  const leverDatums = actieveRegels
    .map((r) => r.geleverdOp)
    .filter((d): d is string => d !== null)
    .sort()
  const laatsteRegelLevering = leverDatums.length > 0 ? (leverDatums[leverDatums.length - 1] ?? null) : null
  const aantalGeleverd = actieveRegels.filter((r) => r.regelStatus === 'GELEVERD').length

  // Een automatisch geleverde bestelling (FO-373) heeft geen geleverdOp: dan de
  // laatste regel-levering. Verwerkt zonder claim (van vóór batch-69): de
  // verwerker telt als wie hem oppakte.
  const stappen: UitrustingTijdlijnStapDTO[] = ZONDER_VOORTGANG.has(bron.status)
    ? []
    : bepaalBestellingStappen({
        status: bron.status,
        ingediendOp: bron.ingediendOp,
        beoordeeldOp: bron.beoordeeldOp,
        verwerktOp: bron.verwerktOp,
        geleverdOp: bron.status === 'GELEVERD' ? (bron.geleverdOp ?? laatsteRegelLevering) : null,
        opgepaktOp: bron.opgepaktOp,
      }).map((s) => {
        const doorNaam = !s.bereikt
          ? null
          : s.stap === 'GOEDGEKEURD'
            ? bron.beoordeeldDoorNaam
            : s.stap === 'OPGEPAKT'
              ? (bron.opgepaktDoorNaam ?? (isVerwerkt ? bron.verwerktDoorNaam : null))
              : s.stap === 'VERWERKT'
                ? bron.verwerktDoorNaam
                : null
        const toelichting =
          s.stap === 'VERWERKT' && s.bereikt
            ? verdelingTekst(bron.regels)
            : s.stap === 'GELEVERD' && !s.bereikt && aantalGeleverd > 0
              ? `${aantalGeleverd} van ${actieveRegels.length} ${actieveRegels.length === 1 ? 'regel' : 'regels'} geleverd`
              : null
        return {
          stap: s.stap,
          label: BESTELLING_STAP_LABELS[s.stap],
          bereikt: s.bereikt,
          datum:
            s.stap === 'OPGEPAKT' && s.bereikt ? (bron.opgepaktOp ?? (isVerwerkt ? bron.verwerktOp : null)) : s.datum,
          doorNaam,
          toelichting,
        }
      })

  const regels: UitrustingTijdlijnRegelDTO[] = bron.regels.map((r) => {
    const alGeleverd = r.alGeleverd ?? null
    const uitVoorraad = alGeleverd ? 0 : Math.max(0, Math.min(r.aantalVoorraad, r.aantal))
    const extern = alGeleverd ? 0 : r.aantal - uitVoorraad
    const bevestigd = r.regelStatus !== 'TE_VERWERKEN'
    return {
      regelId: r.regelId,
      omschrijving: r.omschrijving,
      aantal: r.aantal,
      regelStatus: r.regelStatus,
      aantalUitVoorraad: bevestigd ? uitVoorraad : 0,
      aantalExtern: bevestigd ? extern : 0,
      aantalGeleverd: r.aantalGeleverd,
      geleverdOp: r.geleverdOp,
      extern:
        bevestigd && extern > 0 && r.regelStatus !== 'GESCHRAPT'
          ? {
              besteldOp: r.zending?.verzondenOp ?? null,
              zendingStatus: r.zending?.status ?? null,
              leverancierNaam: opts.publiek ? null : (r.zending?.leverancierNaam ?? null),
              verwachteLevering: r.zending?.gewensteLeverdatum ?? null,
            }
          : null,
      alGeleverd,
    }
  })

  const gebeurtenissen: UitrustingTijdlijnGebeurtenisDTO[] = []
  const heeftIngediendAudit = bron.audits.some((a) => a.action === 'INGEDIEND')
  // Bestellingen van vóór de INGEDIEND-audit (batch-69) krijgen hun indienmoment
  // uit de kolom, zodat de tijdlijn niet halverwege begint.
  if (!heeftIngediendAudit && bron.ingediendOp) {
    gebeurtenissen.push({
      id: `ingediend-${bron.bestellingId}`,
      actie: 'INGEDIEND',
      label: UITRUSTING_BESTELLING_AUDIT_LABELS.INGEDIEND ?? 'Ingediend',
      datum: bron.ingediendOp,
      doorNaam: null,
      toelichting: null,
    })
  }
  const omschrijvingPerRegel = new Map(bron.regels.map((r) => [r.regelId, r.omschrijving]))
  for (const a of bron.audits) {
    if (opts.publiek && !UITRUSTING_PUBLIEKE_AUDIT_ACTIES.has(a.action)) continue
    const notitieZichtbaar = !opts.publiek || PUBLIEKE_NOTITIE_ACTIES.has(a.action)
    // Publiek zonder (interne) notitie: dan in elk geval om welk artikel het gaat.
    const regelOmschrijving = a.regelId ? (omschrijvingPerRegel.get(a.regelId) ?? null) : null
    gebeurtenissen.push({
      id: a.id,
      actie: a.action,
      label: UITRUSTING_BESTELLING_AUDIT_LABELS[a.action] ?? a.action,
      datum: a.createdAt,
      doorNaam: a.performedByNaam,
      toelichting: notitieZichtbaar ? a.note : regelOmschrijving,
    })
  }
  // Nieuwste eerst; bij gelijke tijd de vaste volgorde van binnenkomst omgekeerd.
  const gesorteerd = gebeurtenissen
    .map((g, i) => ({ g, i }))
    .sort((x, y) => (x.g.datum === y.g.datum ? y.i - x.i : x.g.datum < y.g.datum ? 1 : -1))
    .map((x) => x.g)

  return {
    bestellingId: bron.bestellingId,
    status: bron.status,
    fase,
    faseLabel: faseLabel(fase, bron),
    publiek: opts.publiek,
    claim: bron.opgepaktOp ? { doorNaam: bron.opgepaktDoorNaam, op: bron.opgepaktOp } : null,
    stappen,
    regels,
    gebeurtenissen: gesorteerd,
  }
}
